import {
  StationEmptyItemNo,
  StationGameItemNo,
  StationGoalItemNo,
  StationMiniChallengeItemNo,
  StationRewardItemNo,
  StationSlideshowItemNo,
  StationStartItemNo,
  StationSuperChallengeItemNo,
  StationTutorGameItemNo,
  StationType,
} from '../../constants/Games/gameStation';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import {
  AlbumBackgrounds,
  AttachmentImage,
  AttachmentVideo,
  BalanceJson,
  I18n,
  Icon,
  ImageOrSet,
  Locale,
  PartKey,
  Tag,
  UserId,
  Uuid,
} from '../Base';
import { RepositoryElement } from '../Repository';

export type I18nGameSetInfo = I18n<{
  name: string;
  description: string | null;
  previewText?: string;
  pageId?: string;
}>;

export enum AlbumState {
  Assessment = 'assessment',
  Available = 'available',
  Invisible = 'invisible',
  Suspended = 'suspended', // not on backend anymore
  Archived = 'archived', // not on backend anymore
}

export enum AlbumAudience {
  Public = 'public',
  Followers = 'followers',
  Members = 'members',
}

export enum AlbumContentType {
  Both = 'both',
  Game = 'game',
  Slideshow = 'slideshow',
}

export interface PartCount {
  totalCount?: number;
  pairingCount?: number;
}

type PartChallenge = {
  practiceUnlockFee?: BalanceJson;
  practiceReward?: BalanceJson;
} & PartCount;

type MiniChallenge = {
  practiceUnlockFee: BalanceJson;
  practiceReward: BalanceJson;
  interval: number;
};

type PartStatisticsKey =
  | 'itemCount'
  | 'gameCount'
  | 'assistedGameCount'
  | 'slideshowCount'
  | 'rewardJoyPointTotal'
  | 'rewardCreditTotal'
  | 'rewardBreadSliceTotal';

type PartStatistics = Partial<Record<PartStatisticsKey, number>>;

interface AlbumGameDesign {
  thinkingTimeLimit: 'auto' | number;
  backgrounds: AlbumBackgrounds;
  themeColor?: string;
}

export interface StationItemMap {
  start: StationStartItemNo;
  goal: StationGoalItemNo;
  empty: StationEmptyItemNo;
  game: StationGameItemNo;
  slideshow: StationSlideshowItemNo;
  miniChallenge: StationMiniChallengeItemNo;
  superChallenge: StationSuperChallengeItemNo;
  reward: StationRewardItemNo;
  assistedGame: StationTutorGameItemNo;
}

export interface Decorations {
  set: number;
  images: Record<number, AttachmentImage>;
}

export interface StationStyle {
  background: ImageOrSet;
  stations: StationItemMap;
  decorations: Decorations;
}

type I18nStationInfo = I18n<{
  name: string;
}>;

export interface Station {
  type: StationType;
  i18nStationInfo?: I18nStationInfo;
}

interface BubblePath {
  stations: Station[];
  style: StationStyle;
}

interface AlbumContent {
  type: AlbumContentType;
  gameCollectionIds: string[];
  partChallenges?: Partial<Record<PartKey, PartChallenge>>;
  miniChallenges?: Partial<Record<PartKey, MiniChallenge>>;
  partIcons?: Partial<Record<PartKey, string | null>>;
  partStatistics?: Partial<Record<PartKey, PartStatistics>>;
  path: BubblePath[];
  gameDesign: AlbumGameDesign;
  startVideo: AttachmentVideo | null;
}

export enum AlbumChallengeType {
  Both = 'both',
  Challenge = 'challenge',
  Practice = 'practice',
}

type I18nChallengeInfo = I18n<{
  name: string | null;
  description: string | null;
  pageId?: string;
}>;

type I18nCategoryInfo = I18n<{
  name: string;
}>;

interface CategoryCollection {
  gameCollectionId: Uuid;
  parts: Partial<Record<PartKey, PartCount>>;
}

type ChallengeSection = {
  gameCollections: CategoryCollection[];
} & PartCount;

interface ChallengeCategory {
  i18nCategoryInfo: I18nCategoryInfo;
  icon?: Icon;
  sections: ChallengeSection[];
}

export interface ChallengeEstimation {
  maxTotalDurationInMs: number; // defaultValue 1000 * 60 * 60 * 5),
  minTotalDurationInMs: number; // defaultValue 1000 * 60 * 60 * 1),
}

type I18nGradeInfo = I18n<{
  name: string;
  description: string | null;
}>;

interface ChallengeGrade {
  i18nGradeInfo: I18nGradeInfo;
  passingScore: number;
  reward?: BalanceJson;
  nextGameSetId?: Uuid;
  exitGameSetId?: Uuid;
  suggestionClassroomIds?: Uuid[];
}

interface AlbumChallenge {
  type: AlbumChallengeType;
  i18nChallengeInfo: I18nChallengeInfo;
  challengeMaxEntries: number | null;
  challengeEntryFee: BalanceJson;
  challengeReward: BalanceJson;
  practiceUnlockFee: BalanceJson;
  practiceReward: BalanceJson;
  assessmentMaxDepth: number;
  preserveOrder?: number;
  gameCollectionIds?: string[];
  parts?: Partial<Record<PartKey, PartCount>>;
  categories?: ChallengeCategory[];
  estimation?: ChallengeEstimation;
  grades?: ChallengeGrade[];
}

interface Revision {
  period: number;
}

export enum AlbumTournamentType {
  Shield = 'shield',
  Badge = 'badge',
}

interface AlbumTournament {
  tournamentId: Uuid;
  stage: number;
  type: AlbumTournamentType;
  previousAlbumId?: Uuid;
}

export type AlbumStatisticsKey =
  | PartStatisticsKey
  | 'pathChallengeCount'
  | 'superChallengeCount'
  | 'challengeGameCount'
  | 'challengeSlideshowCount'
  | 'likeCount'
  | 'slideshowViewCount'
  | 'videoViewCount'
  | `${
      | 'game'
      | 'assistedGame'
      | 'superChallenge'
      | `pathChallenge_${PartKey}`}${'ScoreTotal' | 'PlayCount'}`
  | `angel${'SuperChallengePlayCount' | `PathChallenge_${PartKey}PlayCount`}`;

export type AlbumStatistics = Partial<Record<AlbumStatisticsKey, number>>;

export interface AlbumJson<T = number> {
  readonly gameSetId: Uuid; // identifier
  classroomId: Uuid;
  createdBy: UserId;
  createdAt: T;
  lastModifiedAt: T;
  startedAt?: T; // primary sorter
  endedAt?: T;
  isSuspended?: boolean;
  i18nGameSetInfo: I18nGameSetInfo;
  state: AlbumState; // not on backend anymore
  partitionId: string | null;
  index: number; // secondary sorter, maximum = 9999999999.999
  precedence: number;
  icons: AttachmentImage[];
  tags: Tag[];
  audience: AlbumAudience;
  viewOnlyAudience?: AlbumAudience;
  isCopyable: boolean;
  content: AlbumContent | null;
  challenge: AlbumChallenge | null;
  leaderboardId: Uuid | null;
  tournament: AlbumTournament | null;
  // var weeklyLeaderboard: Weekly?
  statistics: AlbumStatistics;
  revision: Revision | null;
}

export class Album extends RepositoryElement implements AlbumJson<Date> {
  readonly gameSetId!: Uuid;
  classroomId!: Uuid;
  createdBy!: UserId;
  createdAt!: Date;
  lastModifiedAt!: Date;
  startedAt?: Date;
  endedAt?: Date;
  isSuspended?: boolean;
  i18nGameSetInfo!: I18nGameSetInfo;
  state!: AlbumState;
  partitionId!: string | null;
  index!: number;
  precedence!: number;
  icons!: AttachmentImage[];
  tags!: Tag[];
  audience!: AlbumAudience;
  viewOnlyAudience?: AlbumAudience;
  isCopyable!: boolean;
  content!: AlbumContent | null;
  challenge!: AlbumChallenge | null;
  leaderboardId!: Uuid | null;
  tournament!: AlbumTournament | null;
  statistics!: AlbumStatistics;
  revision!: Revision | null;

  constructor(album: AlbumJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(album) as AlbumJson<Date>);
  }

  id(): string {
    return this.gameSetId;
  }

  toJSON(): AlbumJson {
    return deepTransformDate(this) as unknown as AlbumJson;
  }

  get playableCount(): number {
    if (this.statistics) {
      return (
        (this.statistics.gameCount ?? 0) +
        (this.statistics.assistedGameCount ?? 0) +
        (this.statistics.pathChallengeCount ?? 0) +
        (this.statistics.superChallengeCount ?? 0)
      );
    }

    return (
      this.content?.path
        .flatMap((pathZone) => pathZone.stations)
        .filter((station) =>
          [
            StationType.Game,
            StationType.AssistedGame,
            StationType.PathChallenge,
            StationType.SuperChallenge,
          ].includes(station.type),
        ).length ?? 0
    );
  }

  /**
   * Calculates the total play count for the album by its statistics.
   * @returns The total play count.
   */
  get playCount() {
    if (!this.statistics) {
      return 0;
    }

    const pattern =
      /^(angelSuperChallenge|angelPathChallenge_\d{2}|game|assistedGame|path(Challenge|Practice)_\d{2}|superChallenge)PlayCount$/;
    // challenge|part(Challenge|Practice)_(root|\d{2})|mini(Challenge|Practice)_(root|\d{2})_\d{2}

    return Object.entries(this.statistics)
      .filter(([key]) => pattern.test(key))
      .reduce((sum, [, value]) => sum + (value || 0), 0);
  }
}

export const AlbumExample: Album = new Album({
  gameSetId: 'gameSetId',
  classroomId: 'classroomId',
  createdAt: new Date(),
  createdBy: 'user',
  lastModifiedAt: new Date(),
  startedAt: new Date(),
  i18nGameSetInfo: {
    resources: {
      'en-US': {
        name: 'Game Set name',
        previewText: 'Preview text',
        description: 'description',
      },
    },
    defaultLocale: Locale.US,
  },
  state: AlbumState.Available,
  partitionId: null,
  index: 1,
  precedence: 0,
  icons: [],
  tags: [],
  audience: AlbumAudience.Public,
  isCopyable: true,
  content: {
    type: AlbumContentType.Game,
    gameCollectionIds: ['gameCollectionId'],
    partChallenges: {},
    miniChallenges: {},
    partIcons: {
      '01': 'spelling',
      root: 'spelling',
    },
    partStatistics: {},
    path: [
      {
        style: {
          decorations: {
            images: {},
            set: 3,
          },
          background: {
            set: 3,
          },
          stations: {
            reward: StationRewardItemNo.s1,
            game: StationGameItemNo.s1,
            goal: StationGoalItemNo.s1,
            start: StationStartItemNo.s1,
            slideshow: StationSlideshowItemNo.s1,
            assistedGame: StationTutorGameItemNo.s1,
            miniChallenge: StationMiniChallengeItemNo.s1,
            empty: StationEmptyItemNo.s1,
            superChallenge: StationSuperChallengeItemNo.s1,
          },
        },
        stations: [
          {
            type: StationType.Start,
          },
          {
            type: StationType.AssistedGame,
          },
          {
            type: StationType.Game,
          },
          {
            type: StationType.SuperChallenge,
          },
          {
            type: StationType.Reward,
          },
          {
            type: StationType.Goal,
          },
        ],
      },
    ],
    gameDesign: {
      themeColor: '#34B9AE',
      thinkingTimeLimit: 5,
      backgrounds: {
        entertainment: [
          {
            set: 0,
          },
        ],
        enterprise: [
          {
            set: 11,
          },
        ],
        education: [
          {
            set: 4,
          },
        ],
      },
    },
    startVideo: null,
  },
  challenge: null,
  leaderboardId: null,
  tournament: null,
  statistics: { gamePlayCount: 1, itemCount: 1 },
  revision: { period: 55 },
});
